/**
 * Guard i18n bootstrap.
 *
 * Strategy:
 *  - Static JSON bundles compiled in (no runtime fetches) — 4 languages
 *    × ~150 strings is small (~12 KB gzipped total), and shipping them in
 *    the main bundle avoids a network hop before the first paint.
 *  - Persist user choice in localStorage under `guard-lang` (same pattern
 *    as theme + accepted-terms — no server round trip).
 *  - Default fallback is English; missing keys render the English source so
 *    untranslated screens don't show raw key paths to users.
 *
 * Adding a new language: drop `xx.json` next to en.json (same shape),
 * import it here, append to `resources` + `SUPPORTED_LANGS`, run
 * `python scripts/translate_i18n.py` to regenerate.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import yo from "./locales/yo.json";
import ha from "./locales/ha.json";
import ig from "./locales/ig.json";

export const SUPPORTED_LANGS = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "yo", name: "Yorùbá", flag: "🇳🇬" },
  { code: "ha", name: "Hausa", flag: "🇳🇬" },
  { code: "ig", name: "Igbo", flag: "🇳🇬" },
];

const STORAGE_KEY = "guard-lang";

function detectInitialLang() {
  if (typeof window === "undefined") return "en";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LANGS.some((l) => l.code === saved)) return saved;
  } catch { /* private mode */ }
  // Auto-detect from browser; fall back to English if the user's browser is
  // set to something we don't support (e.g. fr-FR).
  const browser = (typeof navigator !== "undefined" && navigator.language || "").slice(0, 2);
  return SUPPORTED_LANGS.some((l) => l.code === browser) ? browser : "en";
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      yo: { translation: yo },
      ha: { translation: ha },
      ig: { translation: ig },
    },
    lng: detectInitialLang(),
    fallbackLng: "en",
    interpolation: { escapeValue: false }, // React already escapes
    returnEmptyString: false,
  });

// Persist subsequent changes so the picker survives reloads.
i18n.on("languageChanged", (lng) => {
  try { window.localStorage.setItem(STORAGE_KEY, lng); }
  catch { /* private mode */ }
  // Update <html lang> so screen readers + a11y tools announce in the right tongue.
  if (typeof document !== "undefined") document.documentElement.lang = lng;
});

// Set the initial <html lang> too.
if (typeof document !== "undefined") document.documentElement.lang = i18n.language;

export default i18n;
